import { useState } from 'react'
import { X, AlertCircle, Loader, CheckCircle } from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'
import { SecureTextarea } from './SecureInput'
import { scanInput } from '../lib/inputSecurity'

export function JoinExistingProjectModal({ isOpen, onClose }) {
  const [step, setStep] = useState('form') // 'form' or 'success'
  const [formData, setFormData] = useState({
    admin_email: '',
    message: '',
  })
  const [successData, setSuccessData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    setError(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validate email
    if (!formData.admin_email.trim()) {
      setError('Admin email is required')
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.admin_email)) {
      setError('Please enter a valid email address')
      return
    }

    const secErr = scanInput(formData.message)
    if (secErr) { setError(secErr); return }
    setIsLoading(true)
    try {
      const result = await projectsApi.requestProjectAccess(
        formData.admin_email,
        formData.message
      )

      if (result.success) {
        // Move to success screen
        setSuccessData(result.data)
        setStep('success')
      } else {
        setError(result.error || 'Failed to send request')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to send request')
      console.error('Request project access error:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    setStep('form')
    setFormData({ admin_email: '', message: '' })
    setSuccessData(null)
    setError(null)
  }

  const handleClose = () => {
    handleReset()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {/* Form Screen */}
      {step === 'form' && (
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Join Existing Project</h2>
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
            >
              <X size={24} />
            </button>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="m-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Admin Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Admin Email <span className="text-red-600">*</span>
              </label>
              <input
                type="email"
                name="admin_email"
                value={formData.admin_email}
                onChange={handleInputChange}
                placeholder="Enter admin email"
                disabled={isLoading}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                onFocus={(e) => {
                  e.target.style.borderColor = '#358ECD'
                  e.target.style.boxShadow = '0 0 0 3px rgba(53, 142, 205, 0.1)'
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db'
                  e.target.style.boxShadow = 'none'
                }}
              />
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Message
              </label>
              <SecureTextarea
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                placeholder="Optional message to the project admin"
                rows={4}
                disabled={isLoading}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                onBlur={(e) => {
                  e.target.style.borderColor = '#d1d5db'
                  e.target.style.boxShadow = 'none'
                }}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 px-4 py-2 text-white rounded-lg hover:opacity-90 transition flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#358ECD' }}
              >
                {isLoading && <Loader size={16} className="animate-spin" />}
                {isLoading ? 'Sending...' : 'Request Project Access'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Success Screen */}
      {step === 'success' && successData && (
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900">Request Sent Successfully</h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <CheckCircle size={48} className="text-green-500" />
            </div>

            {/* Message */}
            <div className="text-center space-y-4">
              <p className="text-gray-900">
                A request to join the project has been sent successfully to{' '}
                <span className="font-semibold">{successData.admin_email}</span>.
              </p>
              <p className="text-gray-600 text-sm">
                If the request is accepted, the new project will be added to your account
                automatically.
              </p>
            </div>

            {/* Action Button */}
            <button
              onClick={handleClose}
              className="w-full mt-8 px-4 py-3 text-white font-semibold rounded-lg transition duration-200"
              style={{
                backgroundColor: '#358ECD',
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = '#2a6fa3')}
              onMouseLeave={(e) => (e.target.style.backgroundColor = '#358ECD')}
            >
              Back to Projects
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
